/**
 * The lists an agency keeps for itself, drawn as a picker that can be added to.
 *
 * Payout methods and client statuses are the same thing wearing two names: a per-agency list of
 * labels, offered in a dropdown, extended where the choice is being made. Three of them were asked
 * for inside a fortnight and each arrived with the same sentence attached — *be able to add to it
 * on the fly* — so there is one mechanism behind them (`org_options`, ADR-0134) and one control in
 * front of them.
 *
 * **One control matters as much as one table.** Two hand-drawn pickers drift: one of them ends up
 * sorting differently, or offering the archived entries, or losing what was typed when the dialog
 * is cancelled, and the agency learns that "the list on this screen" is a different sort of thing
 * from "the list on that one". Adding a third list to the product is a value in `ORG_OPTION_KINDS`
 * and no work here at all.
 *
 * The way in is the **last option of the select** rather than a button beside it, for the same
 * reason it is in the beneficiary picker: the rows these sit on are already narrow, and a control
 * that is pressed once a quarter should not take width from one that is read every day.
 */

import { useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { ORG_OPTION_LABELS, OPTION_NAME_MAX, type OptionTone, type OrgOptionKind } from "@rentorng/core";
import { api } from "@/lib/api";
import { formValues, useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { Alert, Button, Field, Input, Modal, Select } from "./ui";

export interface OrgOption {
  id: string;
  name: string;
  tone: OptionTone;
  sortOrder: number;
  isSystem: boolean;
  archivedAt: string | null;
}

const ADD = "__add__";

/**
 * One list, by kind.
 *
 * Keyed on the kind so a browser holding the payout methods does not throw them away when a client
 * status is renamed. The starting list is created by the server on this first read, which is why
 * an agency that has never opened either screen still gets a picker with something in it.
 */
export function useOrgOptions(kind: OrgOptionKind, options?: { archived?: boolean }) {
  const archived = Boolean(options?.archived);
  return useQuery({
    queryKey: ["options", kind, archived],
    queryFn: () =>
      api.get<{ kind: OrgOptionKind; label: { one: string; many: string }; options: OrgOption[] }>(
        `/options/${kind}`,
        { archived: archived ? "true" : undefined },
      ),
  });
}

/**
 * How a tone is drawn.
 *
 * Four meanings rather than a colour, because an agency picking a hex code is an agency being asked
 * to design the product. `closed` is the one that earns its own treatment: it means *finished with,
 * still on the record, not in play*, and a hollow ring says that where a second grey disc beside
 * `neutral`'s would say nothing at all.
 */
const TONE: Record<OptionTone, { pill: string; dot: string }> = {
  neutral: { pill: "bg-ink-50 text-ink-600 border-ink-200", dot: "bg-ink-400" },
  active: { pill: "bg-brand-50 text-brand-700 border-brand-200", dot: "bg-brand-500" },
  attention: { pill: "bg-warn-50 text-warn-700 border-warn-200", dot: "bg-warn-500" },
  closed: { pill: "bg-ink-50 text-ink-500 border-ink-200", dot: "border border-ink-400 bg-transparent" },
};

/**
 * The mark itself, beside a name.
 *
 * Nothing is drawn where nothing has been set. A status nobody has chosen is *nothing*, not "Lead":
 * a mark drawn for an unset status would be the software filing people on the agency's behalf, and
 * a list where every row carries a badge is a list where the badge has stopped meaning anything.
 */
export function OptionMark({ name, tone }: { name: string | null | undefined; tone: OptionTone | null | undefined }) {
  if (!name) return null;
  const style = TONE[tone ?? "neutral"] ?? TONE.neutral;
  return (
    <span className={`pill ${style.pill}`} data-option-mark={name}>
      <span className={`size-1.5 rounded-full ${style.dot}`} />
      {name}
    </span>
  );
}

export function OptionPicker({
  kind,
  id,
  name,
  value,
  onChange,
  emptyLabel,
  ariaLabel,
}: {
  kind: OrgOptionKind;
  id?: string;
  /** Set where the picker is read back off the form rather than held in state. */
  name?: string;
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  /** What the "nothing chosen" row says. A status has not been decided; a method was not recorded. */
  emptyLabel: string;
  ariaLabel?: string;
}) {
  const { can } = useSession();
  const { data } = useOrgOptions(kind);
  const [adding, setAdding] = useState(false);
  const list = data?.options ?? [];

  // A permission is filtered, never padlocked: paying us more would not let a viewer edit a list.
  const mayAdd = can("party.edit");

  return (
    <>
      <Select
        id={id}
        name={name}
        aria-label={ariaLabel ?? ORG_OPTION_LABELS[kind].one}
        value={value ?? ""}
        onChange={(event) => {
          if (event.target.value === ADD) {
            setAdding(true);
            return;
          }
          onChange(event.target.value || null);
        }}
      >
        <option value="">{emptyLabel}</option>
        {list.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entry.name}
          </option>
        ))}
        {value && !list.some((entry) => entry.id === value) ? (
          <option value={value}>Previously chosen, no longer on the list</option>
        ) : null}
        {mayAdd ? <option value={ADD}>Add to this list…</option> : null}
      </Select>

      <AddOptionModal kind={kind} open={adding} onClose={() => setAdding(false)} onCreated={(created) => onChange(created)} />
    </>
  );
}

/** Adding an entry, from wherever the choice is being made. */
export function AddOptionModal({
  kind,
  open,
  onClose,
  onCreated,
}: {
  kind: OrgOptionKind;
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const label = ORG_OPTION_LABELS[kind];
  const [tone, setTone] = useState<OptionTone>("neutral");

  const create = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>(`/options/${kind}`, input),
    invalidates: [["options", kind], ["options"], ["parties"]],
    onSuccess: (made: { id?: string } | undefined) => {
      if (made?.id) onCreated(made.id);
      close();
    },
  });

  function close() {
    setTone("neutral");
    create.reset();
    onClose();
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    /*
      Stopped as well as prevented, and it is the second of the two things that make this dialog
      safe inside another form.

      The portal below takes the dialog out of the DOM tree, which is what stops the browser
      submitting the form it was drawn inside. **React events do not follow the DOM tree**: they
      follow the component tree, portal and all, so this submit still reaches the `onSubmit` of the
      form this picker is written inside. Without this line, adding an entry from the client form
      saved the client too — half filled in, while the person was still looking at the dialog.
    */
    event.stopPropagation();
    const values = formValues(event.currentTarget);
    create.mutate({ name: values.optionName, tone });
  };

  /*
    Drawn on the document rather than where it is written, and that is a fix rather than a flourish.

    This dialog is opened from a picker that sits inside somebody else's form — the client form, the
    unit form, the box that marks a payout paid. A `<form>` inside a `<form>` cannot be written in
    markup, but React builds the DOM node by node, so it existed at runtime, and Chrome answered a
    press of the dialog's own button by submitting the outer form: the whole page reloaded, the
    entry was never created, and the half-typed record behind it was gone. It looked exactly like an
    endpoint quietly refusing.

    A portal moves the dialog to the end of `<body>`, so there is no nesting for the browser to
    resolve. It is also where a dialog belongs: it is drawn over everything, and nothing in the
    stacking context it was written inside should be able to clip it.
  */
  return createPortal(
    <Modal open={open} onClose={close} title={`Add a ${label.one.toLowerCase()}`}>
      <form onSubmit={submit} className="space-y-4">
        {create.errorMessage ? <Alert tone="danger">{create.errorMessage}</Alert> : null}

        <Field label="Name" htmlFor="optionName" required error={create.fieldErrors.name}>
          <Input
            id="optionName"
            name="optionName"
            required
            maxLength={OPTION_NAME_MAX}
            placeholder={kind === "payout_method" ? "Mobile transfer" : "Under notice"}
          />
        </Field>

        {/*
          The mark is offered only where one is drawn.

          A payout method is read in a table cell and nowhere else, so asking an agency to choose
          how it should be coloured would be asking them a question with no consequence — and a
          question with no consequence is one more thing between them and the entry they came for.
        */}
        {kind === "client_status" ? (
          <Field
            label="How it is marked"
            htmlFor="optionTone"
            hint="The mark drawn beside a name wherever this is set. Four meanings rather than a colour, so it stays legible on every screen."
          >
            <Select id="optionTone" value={tone} onChange={(event) => setTone(event.target.value as OptionTone)}>
              <option value="neutral">No comment</option>
              <option value="active">A live relationship</option>
              <option value="attention">Somebody has to act</option>
              <option value="closed">Finished with</option>
            </Select>
          </Field>
        ) : null}

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Adding…" : "Add to the list"}
          </Button>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>,
    document.body,
  );
}
